import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useBlueMonk } from '@/contexts/BlueMonkContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, FileText, Package, Users, History, Settings,
  LogOut, BarChart3
} from 'lucide-react';
import { API } from '@/constants/bluemonk';

// Import modular admin components
import {
  AdminDashboardSection,
  AdminInputRequestsSection,
  AdminDeliverablesSection,
  AdminProgressSection,
  AdminClientTeamSection,
  AdminAuditTrailSection,
  AdminSettingsSection,
  AdminInputRequestModal,
  AdminDeliverableModal,
} from '@/components/bluemonk/admin';

const BlueMonkAdminDashboard = () => {
  const navigate = useNavigate();
  const { adminUser, isAdminAuthenticated, adminLogout, getAdminAuthHeader, getAdminActorInfo } = useBlueMonk();
  
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState({ overdueInputs: 0, pendingInputs: 0, awaitingApproval: 0, completed: 0 });
  const [inputRequests, setInputRequests] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [progressItems, setProgressItems] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(true);

  // Modal states
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [deliverableModalOpen, setDeliverableModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isAdminAuthenticated()) {
        navigate('/bluemonk/admin');
      }
      setChecking(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [isAdminAuthenticated, navigate]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAdminAuthHeader();
      
      const [statsRes, inputsRes, delivRes, progressRes, auditRes, configRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/input-requests`, { headers }),
        axios.get(`${API}/deliverables`, { headers }),
        axios.get(`${API}/progress`, { headers }),
        axios.get(`${API}/audit-trail`, { headers }),
        axios.get(`${API}/admin/config`, { headers }),
      ]);

      setStats(statsRes.data);
      setInputRequests(inputsRes.data);
      setDeliverables(delivRes.data);
      setProgressItems(progressRes.data);
      setAuditTrail(auditRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        adminLogout();
        navigate('/bluemonk/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [getAdminAuthHeader, adminLogout, navigate]);

  useEffect(() => {
    if (isAdminAuthenticated()) {
      fetchData();
    }
  }, [isAdminAuthenticated, fetchData]);

  const handleLogout = () => {
    adminLogout();
    navigate('/bluemonk/admin');
  };

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'inputs', label: 'Input Requests', icon: FileText },
    { id: 'deliverables', label: 'Deliverables', icon: Package },
    { id: 'progress', label: 'Progress Tracker', icon: BarChart3 },
    { id: 'client-team', label: 'Client Team', icon: Users },
    { id: 'audit', label: 'Audit Trail', icon: History },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (checking || !adminUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r fixed h-full">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Content Vista" 
              className="h-8 w-auto"
            />
            <div>
              <p className="text-sm font-semibold">BlueMonk Admin</p>
              <p className="text-xs text-slate-500">{adminUser.name}</p>
            </div>
          </div>
        </div>

        <nav className="px-3 space-y-1">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-left ${
                activeSection === item.id
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full"
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 flex-1 p-8">
        {activeSection === 'dashboard' && (
          <AdminDashboardSection 
            stats={stats} 
            auditTrail={auditTrail.slice(0, 10)} 
          />
        )}
        
        {activeSection === 'inputs' && (
          <AdminInputRequestsSection 
            items={inputRequests}
            headers={getAdminAuthHeader()}
            onRefresh={fetchData}
            onEdit={(item) => { setEditingItem(item); setInputModalOpen(true); }}
            onAdd={() => { setEditingItem(null); setInputModalOpen(true); }}
          />
        )}
        
        {activeSection === 'deliverables' && (
          <AdminDeliverablesSection 
            items={deliverables}
            headers={getAdminAuthHeader()}
            actor={getAdminActorInfo()}
            onRefresh={fetchData}
            onEdit={(item) => { setEditingItem(item); setDeliverableModalOpen(true); }}
            onAdd={() => { setEditingItem(null); setDeliverableModalOpen(true); }}
          />
        )}

        {activeSection === 'progress' && (
          <AdminProgressSection 
            items={progressItems}
            headers={getAdminAuthHeader()}
            onRefresh={fetchData}
          />
        )}
        
        {activeSection === 'client-team' && (
          <AdminClientTeamSection 
            config={config}
            headers={getAdminAuthHeader()}
            onRefresh={fetchData}
          />
        )}
        
        {activeSection === 'audit' && (
          <AdminAuditTrailSection 
            items={auditTrail} 
            headers={getAdminAuthHeader()} 
          />
        )}
        
        {activeSection === 'settings' && (
          <AdminSettingsSection 
            config={config}
            headers={getAdminAuthHeader()}
            onRefresh={fetchData}
          />
        )}
      </main>

      {/* Modals */}
      <AdminInputRequestModal 
        open={inputModalOpen}
        onOpenChange={setInputModalOpen}
        item={editingItem}
        headers={getAdminAuthHeader()}
        onSuccess={() => { fetchData(); setInputModalOpen(false); }}
      />

      <AdminDeliverableModal 
        open={deliverableModalOpen}
        onOpenChange={setDeliverableModalOpen}
        item={editingItem}
        headers={getAdminAuthHeader()}
        onSuccess={() => { fetchData(); setDeliverableModalOpen(false); }}
      />
    </div>
  );
};

export default BlueMonkAdminDashboard;
